import React, { useEffect, useMemo, useState, useCallback } from 'react';

import RMSDataGridToolbar from './RMSDataGridToolbar';
import RMSDataGridContent from './RMSDataGridContent';

const RMSSimpleGrid = ({
  primaryKey,
  toolbar = {
    showToolbar: false,
    title: '',
    caption: '',
    searchable: true,
    actions: {
      showAddButton: false,
      onAdd: null,
      showRefreshButton: false,
      onRefresh: null,
    },
    toolbarActions: null,
  },
  features = {
    rowOrder: {
      enableRowReorder: false,
      onSaveOrder: null,
      defaultSortField: 'Code',
      showAtoZButton: false,
    },
  },
  columns,
  rows = [],
  renderActions,
  onRowClick,
  enableColumnResize = false,
  enableColumnReorder = false,
}) => {
  const { showToolbar = false } = toolbar;

  const { rowOrder = {} } = features;

  const { enableRowReorder = false, onSaveOrder, defaultSortField = 'Code' } = rowOrder;

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [tableRows, setTableRows] = useState(rows);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [sortState, setSortState] = useState({ field: null, direction: 'asc' });
  const [columnWidths, setColumnWidths] = useState({});
  const [columnOrder, setColumnOrder] = useState(() => columns.map((column) => column.field));

  useEffect(() => {
    setTableRows(rows);
    setHasOrderChanged(false);
  }, [rows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setColumnOrder((prev) => {
      const fields = columns.map((column) => column.field);
      const stillValid = prev.filter((field) => fields.includes(field));
      const missing = fields.filter((field) => !stillValid.includes(field));
      return [...stillValid, ...missing];
    });
  }, [columns]);

  const orderedColumns = useMemo(() => {
    if (!enableColumnResize && !enableColumnReorder) {
      return columns;
    }

    const columnMap = new Map(columns.map((column) => [column.field, column]));
    const ordered = enableColumnReorder
      ? columnOrder.map((field) => columnMap.get(field)).filter(Boolean)
      : columns;

    return ordered.map((column) => ({
      ...column,
      width: enableColumnResize ? columnWidths[column.field] ?? column.width : column.width,
    }));
  }, [columns, columnOrder, columnWidths, enableColumnResize, enableColumnReorder]);

  const handleColumnResize = useCallback((field, width) => {
    setColumnWidths((prev) => ({ ...prev, [field]: width }));
  }, []);

  const handleColumnReorder = useCallback((sourceField, targetField) => {
    setColumnOrder((prev) => {
      const newOrder = [...prev];
      const fromIndex = newOrder.indexOf(sourceField);
      const toIndex = newOrder.indexOf(targetField);

      if (fromIndex === -1 || toIndex === -1) return prev;

      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, sourceField);

      return newOrder;
    });
  }, []);

  const filteredRows = useMemo(() => {
    if (!searchText) {
      return tableRows;
    }

    return tableRows.filter((row) =>
      Object.values(row).some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(searchText.toLowerCase())
      )
    );
  }, [tableRows, searchText]);

  const sortable = !enableRowReorder;

  const compareValues = (a, b) => {
    if (a === b) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === 'boolean' || typeof b === 'boolean') {
      return (a ? 1 : 0) - (b ? 1 : 0);
    }
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  const sortedRows = useMemo(() => {
    if (!sortable || !sortState.field) {
      return filteredRows;
    }

    const sorted = [...filteredRows].sort((a, b) =>
      compareValues(a[sortState.field], b[sortState.field])
    );

    return sortState.direction === 'desc' ? sorted.reverse() : sorted;
  }, [filteredRows, sortState, sortable]);

  const handleSort = useCallback((field) => {
    setSortState((prev) => {
      if (prev.field !== field) return { field, direction: 'asc' };
      return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  }, []);

  const handleRowsChange = useCallback((updatedRows) => {
    setTableRows(updatedRows);
    setHasOrderChanged(true);
  }, []);

  const handleSaveOrder = useCallback(() => {
    onSaveOrder?.(tableRows);
    setHasOrderChanged(false);
  }, [onSaveOrder, tableRows]);

  const handleSortAZ = useCallback(() => {
    const sortedRows = [...tableRows].sort((a, b) =>
      String(a[defaultSortField] ?? '').localeCompare(String(b[defaultSortField] ?? ''))
    );

    setTableRows(sortedRows);
    setHasOrderChanged(true);
  }, [tableRows, defaultSortField]);

  return (
    <>
      {showToolbar && (
        <RMSDataGridToolbar
          toolbar={toolbar}
          rowOrder={rowOrder}
          rowCount={sortedRows?.length}
          searchText={searchInput}
          onSearchChange={setSearchInput}
          hasOrderChanged={hasOrderChanged}
          onSaveOrder={handleSaveOrder}
          onSortAZ={handleSortAZ}
        />
      )}

      <RMSDataGridContent
        columns={orderedColumns}
        rows={sortedRows}
        enableRowReorder={enableRowReorder}
        onRowsChange={handleRowsChange}
        renderActions={renderActions}
        onRowClick={onRowClick}
        primaryKey={primaryKey}
        sortField={sortable ? sortState.field : null}
        sortDirection={sortState.direction}
        onSort={sortable ? handleSort : null}
        enableColumnResize={enableColumnResize}
        enableColumnReorder={enableColumnReorder}
        onColumnResize={handleColumnResize}
        onColumnReorder={handleColumnReorder}
      />
    </>
  );
};

export default RMSSimpleGrid;
